import React, { useEffect, useRef, useState } from 'react';
import logger from '../utils/logger';
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Line,
  Star,
  Ellipse,
  Rect,
  Text as KonvaText,
  Label,
  Tag,
  Arrow
} from 'react-konva';
import useImage from 'use-image';
import EditShapeModal from './EditSHapeModal';
import '../styles/EditShapeModal.css';
import man from '../assets/body_man.png';
import woman from '../assets/body_woman.png';
import manBack from '../assets/body_man_back.png';
import womanBack from '../assets/body_woman_back.png';
import manHeadNeck from '../assets/body_man_head.png';
import womanHeadNeck from '../assets/body_woman_head.png';
import manUpperBody from '../assets/body_man_upper.png';
import womanUpperBody from '../assets/body_woman_upper.png';
import manLeftArm from '../assets/body_man_leftarm.png';
import womanLeftArm from '../assets/body_woman_leftarm.png';
import manRightArm from '../assets/body_man_rightarm.png';
import womanRightArm from '../assets/body_woman_rightarm.png';
import manLeftLeg from '../assets/body_man_leftleg.png';
import womanLeftLeg from '../assets/body_woman_leftleg.png';
import manRightLeg from '../assets/body_man_rightleg.png';
import womanRightLeg from '../assets/body_woman_rightleg.png';
import logo from '../assets/logo.png';
import { useFormikContext } from 'formik';

const DrawingCanvas = ({ onCanvasReady, gender, setGender, previousDrawings }) => {
  const [canvasHeight, setCanvasHeight] = useState(window.innerHeight);
  const [canvasWidth, setCanvasWidth] = useState(window.innerHeight / 667 * 375);
  const [alertShowPopup, setAlertShowPopup] = useState(false);
  const [tool, setTool] = useState('pencil');
  const [savedDrawings, setSavedDrawings] = useState({});
  const [drawings, setDrawings] = useState([]);
  const eraserRadius = 10;
  const isErasing = useRef(false);
  const [color, setColor] = useState('#ff0000');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showShapePanel, setShowShapePanel] = useState(false);
  const [zoomMode, setZoomMode] = useState(false);
  const [view, setView] = useState('full-body');
  const [isBack, setBack] = useState(0);
  const [currentArrow, setCurrentArrow] = useState(null);
  const arrowStart = useRef(null);
  const isDrawing = useRef(false);
  const stageRefs = useRef({});
  const [showShapeEditor, setShowShapeEditor] = useState(false);
  const [shapeConfigs, setShapeConfigs] = useState({
    'sharp-pain': { size: 10, color: 'red', label: 'Sharp Pain' },
    'dull-ache': { size: 10, color: 'orange', label: 'Dull Ache' },
    'tingling': { size: 10, color: 'blue', label: 'Tingling' },
    'numbness': { size: 10, color: 'green', label: 'Numbness' }
  });
  const { values } = useFormikContext() || { values: {} };
  const [activeLabel, setActiveLabel] = useState(null);
  const partTextStyle = {
    fontFamily: 'Arial',
    fontStyle: 'bold',
    fill: '#333',
    padding: 6
  };

  const handleDownloadWrapper = () => {
    if (zoomMode) {
      // Save current drawings before reset
      saveCurrentViewDrawings();
      handleResetView();
      setAlertShowPopup(true);
      setTimeout(() => {
        setAlertShowPopup(false);
        handleDownload();
      }, 1000);
      return;
    }
    handleDownload();
  };

  useEffect(() => {
    if (onCanvasReady) {
      onCanvasReady({
        download: handleDownloadWrapper
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCanvasReady, zoomMode]);

  useEffect(() => {
    const handleResize = () => {
      const height = Math.min(window.innerHeight, window.screen.height);
      const width = height / 667 * 375;

      setCanvasHeight(height);
      setCanvasWidth(width);
      // Prevent further updates during scroll
      window.removeEventListener('resize', handleResize);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load previous drawings when provided (for follow-up users)
  useEffect(() => {
    logger.log('🎨 [DrawingCanvas] previousDrawings received:', previousDrawings);
    if (previousDrawings && typeof previousDrawings === 'object') {
      logger.log('🎨 [DrawingCanvas] Loading saved drawings...');
      // Load saved drawings from previous submission
      if (previousDrawings.savedDrawings) {
        logger.log('🎨 [DrawingCanvas] savedDrawings keys:', Object.keys(previousDrawings.savedDrawings));
        setSavedDrawings(previousDrawings.savedDrawings);
        // Load drawings for current view
        const currentKey = `${gender}-${view}`;
        const viewDrawings = previousDrawings.savedDrawings[currentKey] || [];
        logger.log('🎨 [DrawingCanvas] Loading drawings for', currentKey, ':', viewDrawings.length, 'items');
        setDrawings(viewDrawings);
      }
      // Load shape configurations if available
      if (previousDrawings.shapeConfigs) {
        logger.log('🎨 [DrawingCanvas] Loading shape configs');
        setShapeConfigs(prev => ({ ...prev, ...previousDrawings.shapeConfigs }));
      }
    }
  }, [previousDrawings]);

  // When view or gender changes, load the saved drawings for that view
  useEffect(() => {
    const currentKey = `${gender}-${view}`;
    if (savedDrawings[currentKey]) {
      logger.log('🎨 [DrawingCanvas] Switching view to', currentKey, '- loading', savedDrawings[currentKey].length, 'drawings');
      setDrawings(savedDrawings[currentKey]);
    }
  }, [gender, view, savedDrawings]);

  const imageMap = {
    man: man,
    woman: woman,
    'man-back': manBack,
    'woman-back': womanBack,
    'man-head-neck': manHeadNeck,
    'woman-head-neck': womanHeadNeck,
    'man-upper-body': manUpperBody,
    'woman-upper-body': womanUpperBody,
    'man-left-arm': manLeftArm,
    'woman-left-arm': womanLeftArm,
    'man-right-arm': manRightArm,
    'woman-right-arm': womanRightArm,
    'man-left-leg': manLeftLeg,
    'woman-left-leg': womanLeftLeg,
    'man-right-leg': manRightLeg,
    'woman-right-leg': womanRightLeg
  };

  const [bodyImage] = useImage(imageMap[`${gender}${view === 'full-body' ? '' : '-' + view}`]);

  useEffect(() => {
    const key = `${gender}-${view}`;
    setDrawings(savedDrawings[key] || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender, view]);

  useEffect(() => {
    // canvas readiness helper
    if (onCanvasReady) {
      onCanvasReady({
        download: handleDownload,
        checkAndShowAlert: () => {
          const currentKey = `${gender}-${view}`;
          const updatedSavedDrawings = {
            ...savedDrawings,
            [currentKey]: drawings
          };
          const hasDrawings = checkIfUserHasDrawnAnythingWithDrawings(updatedSavedDrawings);
          if (!hasDrawings) {
            alert('Please draw your pain symptoms on at least one body view before submitting.');
            return false;
          }
          return true;
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCanvasReady, savedDrawings, drawings, gender, view]);

  const updateShapeConfig = (toolKey, newValues) => {
    setShapeConfigs((prev) => ({
      ...prev,
      [toolKey]: { ...prev[toolKey], ...newValues }
    }));
  };

  const eraseAtPoint = (pos) => {
    const radiusSq = eraserRadius * eraserRadius;
    const updatedDrawings = [];

    for (const d of drawings) {
      if (d.type === 'pencil') {
        const oldPts = d.data.points;
        let currentSegment = [];

        for (let i = 0; i < oldPts.length; i += 2) {
          const x = oldPts[i];
          const y = oldPts[i + 1];
          const dx = x - pos.x;
          const dy = y - pos.y;
          const inside = dx * dx + dy * dy < radiusSq;

          if (!inside) {
            currentSegment.push(x, y);
          } else if (currentSegment.length >= 4) {
            updatedDrawings.push({
              type: 'pencil',
              data: { stroke: d.data.stroke, points: currentSegment }
            });
            currentSegment = [];
          } else {
            currentSegment = [];
          }
        }

        if (currentSegment.length >= 4) {
          updatedDrawings.push({
            type: 'pencil',
            data: { stroke: d.data.stroke, points: currentSegment }
          });
        }
      } else if (d.type === 'shape') {
        const dx = d.data.x - pos.x;
        const dy = d.data.y - pos.y;
        if (dx * dx + dy * dy > radiusSq) {
          updatedDrawings.push(d);
        }
      } else if (d.type === 'arrow') {
        const midX = (d.data.start.x + d.data.end.x) / 2;
        const midY = (d.data.start.y + d.data.end.y) / 2;
        const dx = midX - pos.x;
        const dy = midY - pos.y;
        if (dx * dx + dy * dy > radiusSq) {
          updatedDrawings.push(d);
        }
      } else {
        updatedDrawings.push(d);
      }
    }
    setDrawings(updatedDrawings);
  };

  const handleMouseDown = (e) => {
    const stage = e.target.getStage();
    if (!stage || zoomMode) return;
    const pos = stage.getPointerPosition();
    if (!pos) return;

    if (tool === 'eraser') {
      isErasing.current = true;
      eraseAtPoint(pos);
    } else if (tool === 'pencil') {
      isDrawing.current = true;
      setDrawings((prev) => [
        ...prev,
        {
          type: 'pencil',
          data: {
            points: [pos.x, pos.y],
            stroke: color
          }
        }
      ]);
    } else if (tool === 'arrow') {
      arrowStart.current = pos;
    } else {
      const { size, color: shapeColor } = shapeConfigs[tool] || {};
      setDrawings((prev) => [
        ...prev,
        {
          type: 'shape',
          data: { tool, x: pos.x, y: pos.y, size, color: shapeColor }
        }
      ]);
    }
  };

  const handleMouseMove = (e) => {
    const stage = e.target.getStage();
    if (!stage) return;
    const point = stage.getPointerPosition();
    if (!point) return;

    if (tool === 'eraser' && isErasing.current) {
      eraseAtPoint(point);
      return;
    }

    if (tool === 'pencil' && isDrawing.current) {
      setDrawings((prev) => {
        const last = prev[prev.length - 1];
        if (last?.type !== 'pencil') return prev;
        const updated = {
          ...last,
          data: {
            ...last.data,
            points: [...last.data.points, point.x, point.y]
          }
        };
        return [...prev.slice(0, -1), updated];
      });
    } else if (tool === 'arrow' && arrowStart.current) {
      setCurrentArrow({
        start: arrowStart.current,
        end: point,
        stroke: color
      });
    }
  };

  const handleMouseUp = (e) => {
    if (tool === 'eraser') {
      isErasing.current = false;
    } else if (tool === 'pencil') {
      isDrawing.current = false;
    } else if (tool === 'arrow' && arrowStart.current) {
      const end = e.target.getStage().getPointerPosition();
      setDrawings([...drawings, {
        type: 'arrow',
        data: { start: arrowStart.current, end, stroke: color },
      }]);
      arrowStart.current = null;
      setCurrentArrow(null);
    }
  };

  const saveCurrentViewDrawings = () => {
    const key = `${gender}-${view}`;
    setSavedDrawings((prev) => ({
      ...prev,
      [key]: [...drawings]
    }));
  };

  const handleZoomSelect = (part) => {
    saveCurrentViewDrawings();
    const nextKey = `${gender}-${part}`;
    setDrawings(savedDrawings[nextKey] || []);
    setZoomMode(false);
    setView(part);
  };

  const handleResetView = () => {
    // Save current view drawings before switching
    saveCurrentViewDrawings();
    const nextKey = `${gender}-full-body`;
    setDrawings(savedDrawings[nextKey] || []);
    setZoomMode(false);
    setView('full-body');
  };

  // NEW FUNCTION: Handle Body Region button click with proper saving
  const handleBodyRegionClick = () => {
    // First save current drawings
    saveCurrentViewDrawings();

    // Then switch to full body view if not already there
    if (view !== 'full-body') {
      const nextKey = `${gender}-full-body`;
      setDrawings(savedDrawings[nextKey] || []);
      setView('full-body');
    }

    // Enable zoom mode
    setZoomMode(true);
  };

  const checkIfUserHasDrawnAnythingWithDrawings = (drawingsObj) => {
    const views = [
      'full-body',
      'back',
      'head-neck',
      'upper-body',
      'left-arm',
      'right-arm',
      'left-leg',
      'right-leg'
    ];

    for (const viewName of views) {
      const key = `${gender}-${viewName}`;
      if (drawingsObj && drawingsObj[key] && drawingsObj[key].length > 0) {
        return true;
      }
    }
    return false;
  };

  const handleDownload = async () => {
    // Persist current before capture
    saveCurrentViewDrawings();
    await new Promise((resolve) => setTimeout(resolve, 300));

    const currentKey = `${gender}-${view}`;
    const updatedSavedDrawings = {
      ...savedDrawings,
      [currentKey]: drawings
    };

    const hasAnyDrawings = checkIfUserHasDrawnAnythingWithDrawings(updatedSavedDrawings);
    if (!hasAnyDrawings) {
      alert('Please draw your pain symptoms on at least one body view before generating the diagram.');
      return;
    }

    const jsPDF = (await import('jspdf')).default;
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [600, 912] });

    try {
      const logoImg = await loadImageToBase64(logo);
      const bgImg = await loadImageToBase64(
        'https://storage.googleapis.com/msgsndr/11NLpXQF1SZFaLK9l2gW/media/682c139b438303e8bc1af578.png'
      );

      const views = [
        'full-body',
        'back',
        'head-neck',
        'upper-body',
        'left-arm',
        'right-arm',
        'left-leg',
        'right-leg'
      ];

      const viewLabels = {
        'full-body': 'Front View',
        back: 'Back View',
        'head-neck': 'Front Head & Neck',
        'upper-body': 'Front Upper Body',
        'left-arm': 'Left Arm',
        'right-arm': 'Right Arm',
        'left-leg': 'Left Leg',
        'right-leg': 'Right Leg'
      };

      const originalGender = gender;
      const originalView = view;
      const results = [];

      const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      const waitForStageRender = async (stageKey, maxRetries = 15) => {
        for (let i = 0; i < maxRetries; i++) {
          const stage = stageRefs.current[stageKey];
          if (stage) {
            const layers = stage.getLayers?.() || [];
            if (layers[0]) layers[0].batchDraw?.();
            stage.batchDraw?.();
            await wait(150);
            return stage;
          }
          await wait(100);
        }
        return null;
      };

      for (const v of views) {
        setView(v);
        await wait(450);

        const key = `${gender}-${v}`;
        const viewDrawings = updatedSavedDrawings[key] || [];
        setDrawings(viewDrawings);
        await wait(350);

        const stage = await waitForStageRender(key);
        if (!stage) {
          logger.warn(`Stage not found for ${key}, skipping...`);
          results.push({ view: v, image: null });
          continue;
        }

        try {
          const layers = stage.getLayers?.() || [];
          if (layers[0]) layers[0].batchDraw?.();
          stage.batchDraw?.();
          await wait(200);

          const dataURL = stage.toDataURL({
            pixelRatio: 1.2,
            mimeType: 'image/png',
            quality: 0.9
          });

          if (dataURL && dataURL !== 'data:,' && dataURL.length > 100) {
            results.push({ view: v, image: dataURL });

          } else {
            logger.warn(`Empty or invalid dataURL for ${v}`);
            results.push({ view: v, image: null });
          }
        } catch (error) {
          logger.error(`Error capturing ${v}:`, error);
          results.push({ view: v, image: null });
        }
      }

      // Restore original state
      setView(originalView);
      setGender(originalGender);
      const originalKey = `${originalGender}-${originalView}`;
      const originalDrawings = updatedSavedDrawings[originalKey] || [];
      setDrawings(originalDrawings);
      await wait(300);

      const missingViews = results.filter((r) => !r.image).length;
      if (missingViews > 0) {
        window.parent.postMessage(
          {
            type: 'PAIN_DIAGRAM_INCOMPLETE',
            missingCount: missingViews
          },
          '*'
        );

        const incompleteEvent = new CustomEvent('painDiagramIncomplete', {
          detail: { missingCount: missingViews }
        });
        window.dispatchEvent(incompleteEvent);

        alert(`Your pain diagram is incomplete (missing ${missingViews} views). Please redraw the pain diagram.`);
        return;
      }

      // Layout constants
      const pageW = 600;
      const pageH = 912;
      const paddingX = 50;
      const paddingY = 30;
      const headerHeight = 20;
      const imgW = 225;
      const imgH = 406;
      const colGap = 50;
      const rowGap = 10;
      const legendW = 120;
      const legendH = 130;
      const legendX = (pageW - legendW) / 2;
      const legendY = (pageH - legendH) / 2;

      const addCustomHeader = (doc) => {
        try {
          if (bgImg) {
            doc.addImage(bgImg, 'PNG', 0, 0, 200, 200);
          }
          if (logoImg) {
            doc.addImage(logoImg, 'PNG', paddingX, 20, 100, 30);
          }
        } catch (error) {
          logger.warn('Error adding header images:', error);
        }

        const now = new Date();
        const dateStr = `${now.getMonth() + 1}/${now.getDate()}/${now.getFullYear()}`;
        const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        const fullText = `GENERATED ON: ${dateStr}, ${timeStr}`;

        const textX = pageW - paddingX;
        const textY = 30;

        doc.setFontSize(10);
        doc.setFont('Helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text(fullText, textX, textY, { align: 'right' });

        const lineWidth = 130;
        const lineY = textY + 7;
        const lineXStart = textX - lineWidth;
        const firstPartWidth = lineWidth * 0.3;
        const secondPartWidth = lineWidth * 0.7;

        doc.setDrawColor(54, 56, 69);
        doc.setLineWidth(2);
        doc.line(lineXStart, lineY, lineXStart + firstPartWidth, lineY);

        doc.setDrawColor(68, 145, 202);
        doc.line(lineXStart + firstPartWidth, lineY, lineXStart + lineWidth, lineY);

        doc.setFontSize(20);
        doc.setFont('Helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('PAIN ', 475, 55, { align: 'right' });
        doc.setTextColor(86, 164, 0);
        doc.text('DIAGRAM', 550, 55, { align: 'right' });
      };

      for (let i = 0; i < results.length; i += 4) {
        if (i > 0) pdf.addPage();
        addCustomHeader(pdf);

        results.slice(i, i + 4).forEach((img, idx) => {
          const row = Math.floor(idx / 2);
          const col = idx % 2;

          const x = paddingX + col * (imgW + colGap);
          const y = paddingY + headerHeight + 10 + row * (imgH + rowGap);

          pdf.setDrawColor(0);
          pdf.rect(x, y, imgW, imgH);

          if (img.image) {
            try {
              pdf.addImage(img.image, 'PNG', x, y, imgW, imgH, undefined, 'FAST');
            } catch (error) {
              logger.error(`Error adding image for ${img.view}:`, error);
              pdf.setFontSize(12);
              pdf.setTextColor(128, 128, 128);
              pdf.text('Image not available', x + imgW / 2, y + imgH / 2, { align: 'center' });
            }
          } else {
            pdf.setFontSize(12);
            pdf.setTextColor(128, 128, 128);
            pdf.text('Image not available', x + imgW / 2, y + imgH / 2, { align: 'center' });
          }

          const labelText = viewLabels[img.view] || img.view;
          pdf.setFontSize(14);
          pdf.setTextColor(0, 0, 0);

          if (col === 0) {
            pdf.text(labelText, x + 6, y + 14);
          } else {
            const textWidth = pdf.getTextWidth(labelText);
            pdf.text(labelText, x + imgW - textWidth - 6, y + 14);
          }
        });

        // Legend
        pdf.setDrawColor(0);
        pdf.setFillColor(255, 255, 255);
        pdf.rect(legendX, legendY, legendW, legendH, 'FD');

        pdf.setFontSize(14);
        pdf.setTextColor(0);
        pdf.text('Shape Legend', legendX + legendW / 2, legendY + 14, { align: 'center' });

        let startY = legendY + 30;
        const startX = legendX + 15;

        Object.entries(shapeConfigs).forEach(([toolKey, config]) => {
          pdf.setDrawColor(config.color);
          pdf.setLineWidth(1);

          switch (toolKey) {
            case 'sharp-pain': {
              const side = 10;
              const height = (side * Math.sqrt(3)) / 2;
              const x1 = startX;
              const y1 = startY;
              const x2 = x1 - side / 2;
              const y2 = y1 + height;
              const x3 = x1 + side / 2;
              const y3 = y1 + height;
              pdf.lines(
                [
                  [x2 - x1, y2 - y1],
                  [x3 - x2, y3 - y2],
                  [x1 - x3, y1 - y3]
                ],
                x1,
                y1
              );
              break;
            }
            case 'dull-ache':
              pdf.circle(startX, startY, 5, 'D');
              break;
            case 'tingling':
              pdf.rect(startX - 5, startY - 5, 10, 10, 'D');
              break;
            case 'numbness': {
              const spikes = 5;
              const outer = 6,
                inner = 3;
              const angle = Math.PI / spikes;
              const starPoints = [];
              for (let i = 0; i < 2 * spikes; i++) {
                const r = i % 2 === 0 ? outer : inner;
                const a = i * angle - Math.PI / 2;
                const sx = startX + r * Math.cos(a);
                const sy = startY + r * Math.sin(a);
                starPoints.push([sx, sy]);
              }
              pdf.lines(
                starPoints.map(([px, py], i) => {
                  const next = starPoints[(i + 1) % starPoints.length];
                  return [next[0] - px, next[1] - py];
                }),
                starPoints[0][0],
                starPoints[0][1]
              );
              break;
            }
            default:
              break;
          }

          pdf.setFontSize(12);
          pdf.setTextColor(0);
          pdf.text(config.label, startX + 20, startY + 4);
          startY += 28;
        });
      }

      const fileName = `${gender}-pain-diagram.pdf`;
      const pdfBase64 = pdf.output('datauristring');
      const pdfOutput = pdf.output('arraybuffer');
      const fileSizeInBytes = pdfOutput.byteLength;
      const fileSizeInKB = (fileSizeInBytes / 1024).toFixed(2);

      try {
        window.parent.postMessage(
          {
            type: 'PAIN_DIAGRAM_PDF',
            fileName: fileName,
            fileSize: fileSizeInKB,
            fileData: pdfBase64
          },
          '*'
        );

        const pdfReadyEvent = new CustomEvent('painDiagramPdfReady', {
          detail: { fileName, fileSize: fileSizeInKB }
        });
        window.dispatchEvent(pdfReadyEvent);

        logger.log(`PDF generated successfully: ${fileName} (${fileSizeInKB}KB)`);
      } catch (error) {
        logger.error('Error sending PDF data:', error);
      }
    } catch (error) {
      logger.error('Error in PDF generation:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const loadImageToBase64 = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');

          const maxWidth = 1000;
          const maxHeight = 800;
          let { width, height } = img;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width *= ratio;
            height *= ratio;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          const dataURL = canvas.toDataURL('image/png');
          resolve(dataURL);
        } catch (error) {
          logger.error('Error converting image to base64:', error);
          resolve(null);
        }
      };

      img.onerror = (error) => {
        logger.error('Error loading image:', url, error);
        resolve(null);
      };

      setTimeout(() => {
        if (!img.complete) {
          logger.warn('Image load timeout:', url);
          resolve(null);
        }
      }, 10000);

      img.src = url;
    });
  };

  const renderZoomLabels = () => {
    const positions = {
      headNeck: { x: 0.37, y: 0.07 },
      upperBody: { x: 0.4, y: 0.25 },
      rightArm: { x: 0.23, y: 0.4 },
      leftArm: { x: 0.6, y: 0.4 },
      rightLeg: { x: 0.3, y: 0.6 },
      leftLeg: { x: 0.55, y: 0.6 },
      backButton: { x: 0.32, y: 0.9 }
    };

    const handleLabelClick = (part) => {
      setActiveLabel(part);
      setTimeout(() => setActiveLabel(null), 200);
      handleZoomSelect(part);
    };

    return (
      <>
        {/* Head & Neck */}
        <Label
          x={canvasWidth * positions.headNeck.x}
          y={canvasHeight * positions.headNeck.y}
          onClick={() => handleLabelClick('head-neck')}
          onTap={() => handleLabelClick('head-neck')}
        >
          <Tag
            fill={activeLabel === 'head-neck' ? '#f0f0f0' : 'white'}
            cornerRadius={10}
          />
          <KonvaText text="Head & Neck" fontSize={14}  {...partTextStyle} />
        </Label>

        {/* Upper Body */}
        <Label
          x={canvasWidth * positions.upperBody.x}
          y={canvasHeight * positions.upperBody.y}
          onClick={() => handleLabelClick('upper-body')}
          onTap={() => handleLabelClick('upper-body')}
        >
          <Tag fill={activeLabel === 'upper-body' ? '#f0f0f0' : 'white'} cornerRadius={10} />
          <KonvaText text="Upper Body" fontSize={14} {...partTextStyle} />
        </Label>

        {/* Right Arm */}
        <Label
          x={canvasWidth * positions.rightArm.x}
          y={canvasHeight * positions.rightArm.y}
          onClick={() => handleLabelClick('right-arm')}
          onTap={() => handleLabelClick('right-arm')}
        >
          <Tag fill={activeLabel === 'right-arm' ? '#f0f0f0' : 'white'} cornerRadius={10} />
          <KonvaText text="Right Arm" fontSize={14} {...partTextStyle} />
        </Label>

        {/* Left Arm */}
        <Label
          x={canvasWidth * positions.leftArm.x}
          y={canvasHeight * positions.leftArm.y}
          onClick={() => handleLabelClick('left-arm')}
          onTap={() => handleLabelClick('left-arm')}
        >
          <Tag fill={activeLabel === 'left-arm' ? '#f0f0f0' : 'white'} cornerRadius={10} />
          <KonvaText text="Left Arm" fontSize={14} {...partTextStyle} />
        </Label>

        {/* Right Leg */}
        <Label
          x={canvasWidth * positions.rightLeg.x}
          y={canvasHeight * positions.rightLeg.y}
          onClick={() => handleLabelClick('right-leg')}
          onTap={() => handleLabelClick('right-leg')}
        >
          <Tag fill={activeLabel === 'right-leg' ? '#f0f0f0' : 'white'} cornerRadius={10} />
          <KonvaText text="Right Leg" fontSize={14} {...partTextStyle} />
        </Label>

        {/* Left Leg */}
        <Label
          x={canvasWidth * positions.leftLeg.x}
          y={canvasHeight * positions.leftLeg.y}
          onClick={() => handleLabelClick('left-leg')}
          onTap={() => handleLabelClick('left-leg')}
        >
          <Tag fill={activeLabel === 'left-leg' ? '#f0f0f0' : 'white'} cornerRadius={10} />
          <KonvaText text="Left Leg" fontSize={14} {...partTextStyle} />
        </Label>

        {/* Back to Full Body */}
        <Label
          x={canvasWidth * positions.backButton.x}
          y={canvasHeight * positions.backButton.y}
          onClick={handleResetView}
          onTap={handleResetView}
        >
          <Tag fill="#0772b6" cornerRadius={10} />
          <KonvaText text="Back to Full Body" fontSize={16} fontFamily="Arial" fill="white" padding={8} />
        </Label>
      </>
    );
  };

const renderRightLeftLabels = () => {
  switch (view) {
    case 'full-body':
      return (
        <div className="Left-Right-label">
          <div className="w-65">Right</div>
          <div className="w-65">Left</div>
        </div>
      );

    case 'back':
      return (
        <div className="Left-Right-label">
          <div className="w-65">Left</div>
          <div className="w-65">Right</div>
        </div>
      );

    case 'left-arm':
      return (
        <div className="Left-Right-label">
          <div className="w-65">Left Arm</div>
        </div>
      );

    case 'right-arm':
      return (
        <div className="Left-Right-label">
          <div className="w-65">Right Arm</div>
        </div>
      );

    case 'left-leg':
      return (
        <div className="Left-Right-label">
          <div className="w-65">Left Leg</div>
        </div>
      );

    case 'right-leg':
      return (
        <div className="Left-Right-label">
          <div className="w-65">Right Leg</div>
        </div>
      );

    case 'upper-body':
      return (
        <div className="Left-Right-label">
          <div className="w-65">Right</div>
          <div className="w-65">Left</div>
        </div>
      );

    case 'head-neck':
      return (
        <div className="Left-Right-label">
          <div className="w-65">Right</div>
          <div className="w-65">Left</div>
        </div>
      );

    default:
      return null;
  }
};

  return (
    <div className="drawing-wrapper">
      <div className="p-d-logo-img">
        <img src={logo} alt="Logo" />
      </div>

      <div>
        {renderRightLeftLabels()}
      </div>

      <div className="canvas-container">
        <Stage
          width={canvasWidth}
          height={canvasHeight}
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
          onMouseMove={handleMouseMove}
          onTouchMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchEnd={handleMouseUp}
          ref={(node) => {
            // store the Konva.Stage instance directly
            const key = `${gender}-${view}`;
            stageRefs.current[key] = node ? node.getStage() : null;
          }}
          style={{ touchAction: 'none' }}
        >
          <Layer>
            {bodyImage && (
              <KonvaImage
                image={bodyImage}
                width={canvasWidth * 0.9}
                height={canvasHeight * 0.9}
                x={(canvasWidth - canvasWidth * 0.9) / 2}
                y={(canvasHeight - canvasHeight * 0.9) / 2}
                opacity={zoomMode ? 0.4 : 0.6}
                listening={false}
              />
            )}

            {currentArrow && (
              <Arrow
                points={[currentArrow.start.x, currentArrow.start.y, currentArrow.end.x, currentArrow.end.y]}
                stroke={currentArrow.stroke}
                strokeWidth={2}
                pointerLength={10}
                pointerWidth={8}
                dash={[4, 4]}
              />
            )}

            {drawings.map((item, i) => {
              const d = item.data;

              if (item.type === 'arrow' && !zoomMode) {
                const { start, end, stroke } = item.data;
                return (
                  <Arrow
                    key={`arrow-${i}`}
                    points={[start.x, start.y, end.x, end.y]}
                    stroke={stroke}
                    strokeWidth={2}
                    pointerLength={10}
                    pointerWidth={8}
                    fill={stroke}
                  />
                );
              }

              if (item.type === 'pencil' && !zoomMode) {
                const { points, stroke } = d;
                return (
                  <Line
                    key={`line-${i}`}
                    points={points}
                    stroke={stroke}
                    strokeWidth={2}
                    tension={0.5}
                    lineCap="round"
                    lineJoin="round"
                    globalCompositeOperation="source-over"
                  />
                );
              }

              if (item.type === 'shape' && !zoomMode) {
                const { tool: shapeTool, x, y, size = 10, color: shapeColor = '#ff0000' } = d;

                switch (shapeTool) {
                  case 'sharp-pain':
                    return (
                      <Line
                        key={`tri-${i}`}
                        points={[x, y - size, x - size, y + size, x + size, y + size]}
                        closed
                        stroke={shapeColor}
                        fill="transparent"
                        strokeWidth={2}
                      />
                    );
                  case 'dull-ache':
                    return (
                      <Ellipse
                        key={`ellipse-${i}`}
                        x={x}
                        y={y}
                        radiusX={size}
                        radiusY={size}
                        stroke={shapeColor}
                        fill="transparent"
                        strokeWidth={2}
                      />
                    );
                  case 'tingling':
                    return (
                      <Rect
                        key={`rect-${i}`}
                        x={x - size}
                        y={y - size}
                        width={size * 2}
                        height={size * 2}
                        stroke={shapeColor}
                        fill="transparent"
                        strokeWidth={2}
                      />
                    );
                  case 'numbness':
                    return (
                      <Star
                        key={`star-${i}`}
                        x={x}
                        y={y}
                        numPoints={5}
                        innerRadius={size / 2}
                        outerRadius={size}
                        stroke={shapeColor}
                        fill="transparent"
                        strokeWidth={2}
                      />
                    );
                  default:
                    return null;
                }
              }

              return null;
            })}

            {view === 'full-body' && zoomMode && renderZoomLabels()}
          </Layer>
        </Stage>
      </div>

      <div className="tool-panel left">
        <div className="color-button-wrapper">
          <button className="color-main-button all-btn" onClick={() => setShowColorPicker(!showColorPicker)}>
            <div className="color-ring">
              <div className="inner-ring" style={{ backgroundColor: color }} />
            </div>
            <span className="tool-name"> Color Picker </span>
          </button>
          {showColorPicker && (
            <div className="color-options">
              {['#ff0000', '#ffa500', '#ffff00', '#00ff00', '#0000ff', '#ff00ff'].map((c) => (
                <div
                  key={c}
                  className="color-circle"
                  style={{ backgroundColor: c, border: color === c ? '2px solid #000' : '1px solid #aaa' }}
                  onClick={() => {
                    setColor(c);
                    setShowColorPicker(false);
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <button onClick={() => setShowShapePanel(!showShapePanel)} className="icon-button all-btn">
          <img src="/icons/tool.png" alt="Shapes" className="icon-img" />
          <span className="tool-name"> Shapes </span>
        </button>

        {showShapePanel && (
          <div className="shape-popup">
            <button
              className="modal-close all-btn"
              onClick={() => {
                setShowShapePanel((prev) => {
                  const newState = !prev;
                  if (!newState) setShowShapeEditor(false);
                  return newState;
                });
              }}
            >
              ✖
            </button>

            <button
              className="optionBtn all-btn"
              onClick={() => {
                setTool('sharp-pain');
                setShowShapePanel((prev) => {
                  const newState = !prev;
                  if (!newState) setShowShapeEditor(false);
                  return newState;
                });
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24">
                <polygon points="12,4 4,20 20,20" stroke={shapeConfigs['sharp-pain']?.color || 'red'} fill="white" strokeWidth="3" />
              </svg>
              <span>{shapeConfigs['sharp-pain'].label}</span>
            </button>

            <button
              className="optionBtn all-btn"
              onClick={() => {
                setTool('dull-ache');
                setShowShapePanel((prev) => {
                  const newState = !prev;
                  if (!newState) setShowShapeEditor(false);
                  return newState;
                });
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke={shapeConfigs['dull-ache']?.color || 'orange'} fill="white" strokeWidth="3" />
              </svg>
              <span>{shapeConfigs['dull-ache'].label}</span>
            </button>

            <button
              className="optionBtn all-btn"
              onClick={() => {
                setTool('tingling');
                setShowShapePanel((prev) => {
                  const newState = !prev;
                  if (!newState) setShowShapeEditor(false);
                  return newState;
                });
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24">
                <rect x="4" y="4" width="16" height="16" stroke={shapeConfigs['tingling']?.color || 'blue'} fill="white" strokeWidth="3" />
              </svg>
              <span>{shapeConfigs['tingling'].label}</span>
            </button>

            <button
              className="optionBtn all-btn"
              onClick={() => {
                setTool('numbness');
                setShowShapePanel((prev) => {
                  const newState = !prev;
                  if (!newState) setShowShapeEditor(false);
                  return newState;
                });
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24">
                <polygon
                  stroke={shapeConfigs['numbness']?.color || 'green'}
                  fill="white"
                  strokeWidth="3"
                  points="
                    12,2
                    14.7,8.5
                    22,9.3
                    16.5,14.1
                    18.2,21.5
                    12,17.8
                    5.8,21.5
                    7.5,14.1
                    2,9.3
                    9.3,8.5
                  "
                />
              </svg>
              <span>{shapeConfigs['numbness'].label}</span>
            </button>

            <div style={{ height: '2px', background: '#CCC' }} />

            <button className="optionBtn all-btn" onClick={() => setShowShapeEditor((prev) => !prev)}>
              <img src="/icons/pencil-81-512.png" alt="Edit Shape" className="icon-img-shape" />
              <span> Edit Shape</span>
            </button>

            {showShapeEditor && (
              <EditShapeModal
                tool={tool}
                config={shapeConfigs[tool]}
                onUpdate={(newConfig) => updateShapeConfig(tool, newConfig)}
                onClose={() => setShowShapeEditor(false)}
              />
            )}
          </div>
        )}

        <button
          className="all-btn"
          onClick={() => {
            if (isBack === 0) {
              setBack(1);
              handleZoomSelect('back');
            } else {
              setBack(0);
              handleZoomSelect('full-body');
            }
          }}
        >
          <img src="/icons/refresh-109-512.png" alt="Front/Back" className="icon-img" />
          <span className="tool-name"> Front / Back </span>
        </button>

        {['man', 'woman'].includes(values.gender) && (
          <div className="gender-toggle-row d-flex-g">
            <button
              className={`gender-btn all-btn ${gender === 'man' ? 'selected' : ''}`}
              onClick={() => {
                saveCurrentViewDrawings();
                const nextKey = `man-${view}`;
                setGender('man');
                setDrawings(savedDrawings[nextKey] || []);
              }}
            >
              <img src="/icons/male-21-512.png" alt="Male" className="icon-img" />
              <span className="tool-name"> Male </span>
            </button>
            <button
              className={`gender-btn all-btn ${gender === 'woman' ? 'selected' : ''}`}
              onClick={() => {
                saveCurrentViewDrawings();
                const nextKey = `woman-${view}`;
                setGender('woman');
                setDrawings(savedDrawings[nextKey] || []);
              }}
            >
              <img src="/icons/female-28-512.png" alt="Female" className="icon-img" />
              <span className="tool-name"> Female </span>
            </button>
          </div>
        )}
      </div>

      <div className="tool-panel right">
        <button className="all-btn" onClick={() => setDrawings((prev) => prev.slice(0, -1))}>
          <img src="/icons/reset-16-128.png" alt="Undo" className="icon-img" />
          <span className="tool-name"> Undo </span>
        </button>

        <button onClick={() => setTool('arrow')} className={`icon-button all-btn ${tool === 'arrow' ? 'selected' : ''}`}>
          <img src="/icons/arrow1.png" alt="Arrow" className="icon-img" />
          <span className="tool-name"> Arrow </span>
        </button>

        <button onClick={() => setTool('pencil')} className={`icon-button all-btn ${tool === 'pencil' ? 'selected' : ''}`}>
          <img src="/icons/pencil-81-512.png" alt="Pencil" className="icon-img" />
          <span className="tool-name"> Pencil </span>
        </button>

        <button onClick={() => setTool('eraser')} className={`icon-button all-btn ${tool === 'eraser' ? 'selected' : ''}`}>
          <img src="/icons/eraser.png" alt="Eraser" className="icon-img" />
          <span className="tool-name"> Eraser </span>
        </button>

        <button
          className="all-btn"
          onClick={handleBodyRegionClick}
        >
          <img src="/icons/zoom-in-zoom-in-512.png" alt="Body Part" className="icon-img" />
          <span className="tool-name"> Body Region </span>
        </button>

      </div>

      {alertShowPopup && (
        <div className="alert-box">
          <div className="alert-container">
            <p className="alert-text">Please select a body part or reset to full body view before sending</p>
            <div className="alert-buttons">
              <button className="alert-btn" onClick={() => setAlertShowPopup(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawingCanvas;